// Registro dei messaggi: costruisce l'albero per lingua dai file `locales/<lingua>/<namespace>.json` e lo unisce al vecchio
// `messages.js` (congelato, si elimina a fine fase). Funzioni pure: il caricamento dei file sta in `messaggi.ts`.
//
// Regola dei file: `<namespace>.json` finisce sotto la chiave `<namespace>` (dashboard.json → `dashboard.titolo`);
// `radice.json` è l'eccezione: le sue chiavi stanno al primo livello (le chiavi piatte storiche, es. `cambio_lingua`).
// Catena di ripiego: lingua attiva (nuovo, poi vecchio), poi `it` (nuovo, poi vecchio): è l'unione profonda con vincita del
// nuovo, più il `fallbackLocale: "it"` di vue-i18n.

// Gli elenchi (es. i nomi dei giorni di PrimeVue) sono valori: non si scendono mai dentro.
export type Messaggio = string | string[] | AlberoMessaggi;
export interface AlberoMessaggi {
  [chiave: string]: Messaggio;
}
export type MessaggiPerLingua = Record<string, AlberoMessaggi>;

export const NAMESPACE_RADICE = "radice";

// `./locales/it/dashboard.json` → { lingua: "it", namespace: "dashboard" }
const PERCORSO_LOCALE = /(?:^|\/)locales\/([^/]+)\/([^/]+)\.json$/;

export function èAlbero(valore: unknown): valore is AlberoMessaggi {
  return typeof valore === "object" && valore !== null && !Array.isArray(valore);
}

// Unione profonda: in caso di conflitto vince `sorgente`. Non modifica gli argomenti.
export function unisci(base: AlberoMessaggi, sorgente: AlberoMessaggi): AlberoMessaggi {
  const risultato: AlberoMessaggi = { ...base };
  for (const [chiave, valore] of Object.entries(sorgente)) {
    const esistente = risultato[chiave];
    risultato[chiave] = èAlbero(valore) && èAlbero(esistente) ? unisci(esistente, valore) : valore;
  }
  return risultato;
}

// `moduli`: risultato di `import.meta.glob("./locales/*/*.json", { eager: true, import: "default" })`.
export function costruisciMessaggi(moduli: Record<string, unknown>): MessaggiPerLingua {
  const perLingua: MessaggiPerLingua = {};
  for (const [percorso, contenuto] of Object.entries(moduli)) {
    const trovato = PERCORSO_LOCALE.exec(percorso);
    if (!trovato) throw new Error(`File dei messaggi fuori posto: ${percorso}`);
    if (!èAlbero(contenuto)) throw new Error(`Il file ${percorso} deve contenere un oggetto`);
    const [, lingua, namespace] = trovato as unknown as [string, string, string];
    const aggiunta = namespace === NAMESPACE_RADICE ? contenuto : { [namespace]: contenuto };
    perLingua[lingua] = unisci(perLingua[lingua] ?? {}, aggiunta);
  }
  return perLingua;
}

// Nuovo sopra vecchio, per ogni lingua presente in uno dei due.
export function conRipiegoSulVecchio(nuovi: MessaggiPerLingua, vecchi: MessaggiPerLingua): MessaggiPerLingua {
  const risultato: MessaggiPerLingua = {};
  for (const lingua of new Set([...Object.keys(vecchi), ...Object.keys(nuovi)])) {
    risultato[lingua] = unisci(vecchi[lingua] ?? {}, nuovi[lingua] ?? {});
  }
  return risultato;
}

// Valore di una chiave puntata (`modulo.sezione.chiave`), `undefined` se manca.
export function leggiChiave(albero: AlberoMessaggi, chiave: string): Messaggio | undefined {
  let corrente: Messaggio | undefined = albero;
  for (const segmento of chiave.split(".")) {
    if (!èAlbero(corrente)) return undefined;
    corrente = corrente[segmento];
  }
  return corrente;
}
